<template>
  <n-input :value="value" @update:value="onTextInput" @blur="onInputBlur"></n-input>
</template>
<script lang="ts" setup>
import { watch } from "vue"

const props = withDefaults(
  defineProps<{
    value: string
    format?: "integer" | "decimal"
    editable?: boolean
    min?: number
    max?: number
  }>(),
  {
    value: "",
    format: "integer",
    editable: true,
  }
)

const emits = defineEmits<{
  "update:value": [value: string]
  change: [value: string]
}>()

const onTextInput = (text: string) => {
  if (!props.editable) return
  const cleaned = sanitizeInput(text, props.format)
  emitValue(cleaned)
}

function sanitizeInput(text: string, format: "integer" | "decimal") {
  let sanitizedText = text
  sanitizedText = normalizeInput(sanitizedText)
  if (format === "integer") {
    sanitizedText = sanitizedText.replace(/\./g, "")
  } else {
    const parts = sanitizedText.split(".")
    if (parts.length > 2) {
      sanitizedText = parts[0] + "." + parts.slice(1).join("")
    }
  }

  return sanitizedText
}

function normalizeInput(text: string): string {
  let result = text
  // 过滤非法字符，只保留数字、小数点和负号
  result = result.replace(/[^0-9.-]/g, "")
  // 规范负号位置，确保负号只能出现在开头
  result = result.replace(/(?!^)-/g, "")
  return result
}

function formatInteger(text: string): string {
  let result = text
  // 移除所有非数字和非负号字符
  result = result.replace(/[^-\d]/g, "")
  // 只保留开头的负号，移除其他位置的负号
  result = result.replace(/(?!^)-/g, "")
  // 处理单独的负号情况
  result = result.replace(/^-$/, "")
  // 移除所有小数点
  result = result.replace(/\./g, "")
  // 移除前导零（保留至少一位数字）
  result = result.replace(/^(-?)0+(\d)/, "$1$2")
  // 处理全零的情况，确保至少保留一个零
  result = result.replace(/^(-?)0+$/, "$10")
  // 将-0转换为0
  result = result.replace(/^-0$/, "0")
  return result
}

function formatDecimal(text: string): string {
  let result = text
  // 处理多个小数点的情况，将第一个小数点后的所有内容合并
  const parts = result.split(".")
  result = parts[0] + "." + parts.slice(1).join("")
  // 移除整数部分的前导零
  result = result.replace(/^(-?)0+(\d)/, "$1$2")
  // 处理全零的情况
  result = result.replace(/^(-?)0+$/, "$10")
  // 处理以小数点开头的情况（如".5"转换为"0.5"）
  result = result.replace(/^\./, "0.")
  // 移除末尾的小数点
  result = result.replace(/\.$/, "")
  // 移除末尾无意义的零（如"1.500"转换为"1.5"）
  result = result.replace(/\.0*$/, "")
  // 移除小数部分末尾的零
  result = result.replace(/(\.\d*?[1-9])0+$/, "$1")
  // 处理负零的情况，将"-0"或"-0.0"等转换为"0"
  if (/^-0(\.0+)?$/.test(result)) {
    result = result.replace(/^-/, "")
  }
  // 如果结果中不包含任何数字，则返回空字符串
  if (!/[0-9]/.test(result)) return ""

  return result
}

const onInputBlur = () => {
  if (!props.editable) return
  const result = normalizeInput(props.value)
  let formatted = props.format === "integer" ? formatInteger(result) : formatDecimal(result)

  if (formatted) {
    const numValue = Number(formatted)
    if (!isNaN(numValue)) {
      console.log("numValue:", numValue)
      if (props.min !== undefined && numValue < props.min) {
        formatted = String(props.min)
      }
      if (props.max !== undefined && numValue > props.max) {
        formatted = String(props.max)
        console.log(props.max, typeof props.max)
        console.log("formatted:", String(props.max))
      }
    }
  }

  emitValue(formatted)
}

const emitValue = (value: string) => {
  console.log("emitValue:", value)
  emits("update:value", value)
}

watch(
  () => props.format,
  (newF, oldF) => {
    if (oldF == "decimal" && newF == "integer") {
      emitValue(formatInteger(props.value))
    }
  }
)
</script>
<style scoped></style>
